import { useMemo } from "react";
import { ServerInferResponseBody } from "@ts-rest/core";
import { VStack } from "@design-system/VStack/VStack";
import { useClasses } from "@api/class.api";
import { classContract } from "@common/contracts/class.contract";
import { Class } from "@common/types/class";
import { pluralize } from "@common/helpers/string";
import { ClassWithChildren } from "@helpers/class";
import { Segment, Subsegment } from "@helpers/segment";
import { SegmentBar } from "@design-system/SegmentBar/SegmentBar";
import { buildSubtreeCountMap } from "./viewUtils";
import styles from "./ClassesStats.module.scss";

interface Props {
    statsMap: Map<
        string,
        ServerInferResponseBody<typeof classContract.getClassStatistics>[number]
    >;
    stats: ServerInferResponseBody<typeof classContract.getClassStatistics>;
    total: number;
    classes: Array<{ id: string }>;
    toggleClass: (categoryId: string) => void;
    classesMapMock?: Map<string, ClassWithChildren>;
}

const ClassStatTooltip = ({
    classItem,
    valueOverride,
    subtitle,
}: {
    classItem: Class;
    valueOverride: string;
    subtitle: React.ReactNode;
}) => (
    <VStack className={styles.classTooltip}>
        <span className={styles.headline}>{classItem.label}</span>
        <span className={styles.value}>{valueOverride}</span>
        <span className={styles.subValue}>{subtitle}</span>
    </VStack>
);

const formatPercentage = (count: number, total: number): string =>
    `${((count / total) * 100).toFixed(2)}%`;

const formatCountSubtitle = (count: number): string =>
    `${count} ${pluralize("transaction", count)}`;

export const ClassesStats: React.FC<Props> = ({
    statsMap,
    total,
    classes,
    toggleClass,
    stats,
    classesMapMock,
}) => {
    const { classesMap: classesMapFromHook } = useClasses();

    const classesMap = classesMapMock ?? classesMapFromHook;

    // The backend only fills `groupCount` for roots, so we compute every node's
    // subtree transaction count client-side from the recursive class tree and
    // each node's direct `count`. These are the same subtree totals shown in
    // the table cells (`groupTransactionsCount`).
    const subtreeCountById = useMemo(() => {
        const directCountById = new Map(
            stats.map((stat) => [stat.id, stat.count]),
        );
        const roots = stats
            .filter((stat) => !stat.parentClassId)
            .map((stat) => classesMap.get(stat.id))
            .filter(
                (classItem): classItem is ClassWithChildren =>
                    classItem !== undefined,
            );
        return buildSubtreeCountMap(roots, directCountById);
    }, [stats, classesMap]);

    const segments = useMemo(() => {
        if (!total) {
            return [];
        }

        return stats
            .filter((stat) => !stat.parentClassId)
            .map((stat) => {
                const classItem = classesMap.get(stat.id);

                if (!classItem) {
                    return;
                }

                const isHidden = !classes.some(({ id }) => id === classItem.id);

                // A root's headline reflects its whole subtree: a root can have
                // 0 direct transactions while its descendants hold them, so
                // using the direct `count` would wrongly show 0.
                const subtreeCount = subtreeCountById.get(classItem.id) ?? 0;
                const ownCount = statsMap.get(classItem.id)?.count ?? 0;

                // Subsegments break the subtree total down by each visible
                // direct child's own subtree count. Because
                // subtree(root) = ownCount + Σ subtree(child), adding the root's
                // own transactions as the first subsegment makes the breakdown
                // sum back to the headline.
                const subsegments: Subsegment[] = classItem.children
                    .filter(
                        (child) =>
                            classes.some(
                                // eslint-disable-next-line sonarjs/no-nested-functions
                                ({ id }) => id === child.id,
                            ) && (subtreeCountById.get(child.id) ?? 0) > 0,
                    )
                    .map((child) => {
                        const childSubtreeCount =
                            subtreeCountById.get(child.id) ?? 0;
                        return {
                            id: child.id,
                            label: child.label,
                            value: childSubtreeCount,
                            valueOverride: formatPercentage(
                                childSubtreeCount,
                                total,
                            ),
                            subtitle: formatCountSubtitle(childSubtreeCount),
                            tooltip: (
                                <ClassStatTooltip
                                    classItem={child}
                                    valueOverride={formatPercentage(
                                        childSubtreeCount,
                                        total,
                                    )}
                                    subtitle={formatCountSubtitle(
                                        childSubtreeCount,
                                    )}
                                />
                            ),
                        };
                    });

                const segment: Segment & {
                    isHidden?: boolean;
                } = {
                    id: classItem.id,
                    label: classItem.label,
                    value: isHidden ? 0 : subtreeCount,
                    valueOverride: formatPercentage(subtreeCount, total),
                    subtitle: formatCountSubtitle(subtreeCount),
                    colors: {
                        start: classItem.iconColor,
                        subtitle: "#0f182680",
                    },
                    isHidden,
                    subsegments,
                };

                segment.tooltip = (
                    <ClassStatTooltip
                        classItem={classItem}
                        valueOverride={segment.valueOverride ?? ""}
                        subtitle={segment.subtitle}
                    />
                );

                if (subsegments.length > 0 && !isHidden && ownCount > 0) {
                    subsegments.unshift({
                        id: classItem.id,
                        label: classItem.label,
                        value: ownCount,
                        valueOverride: formatPercentage(ownCount, total),
                        subtitle: formatCountSubtitle(ownCount),
                        tooltip: (
                            <ClassStatTooltip
                                classItem={classItem}
                                valueOverride={formatPercentage(
                                    ownCount,
                                    total,
                                )}
                                subtitle={formatCountSubtitle(ownCount)}
                            />
                        ),
                    });
                }

                return segment;
            })
            .filter((segment) => segment !== undefined);
    }, [total, stats, classesMap, classes, statsMap, subtreeCountById]);

    return (
        <div className={styles.classStatsContainer}>
            <SegmentBar
                className={styles.classesStats}
                segments={segments}
                hasMinimumWidth
                showSubsegmentsOnHover
                onSegmentClick={toggleClass}
            />
        </div>
    );
};
